import * as React from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { SidebarNavItem } from './sidebar-nav-item'
import { SidebarSection } from './sidebar-section'
import type {
  SidebarEntry,
  SidebarNavEntry,
  SidebarNavItemFields,
  SidebarSectionEntry,
} from './sidebar.types'

export interface SidebarProps {
  logo: React.ReactNode
  entries: SidebarEntry[]
  bottomItems: SidebarNavItemFields[]
  className?: string
  collapsed?: boolean
  /** Rendered in the header row when expanded (e.g. expand/collapse sidebar button). */
  headerAction?: React.ReactNode
  /** Optional tenant switcher rendered between the header and nav section. */
  tenantSwitcher?: React.ReactNode
}

type EntryGroup = {
  section?: SidebarSectionEntry
  items: SidebarNavEntry[]
}

function groupEntries(entries: SidebarEntry[]): EntryGroup[] {
  return entries.reduce<EntryGroup[]>((groups, entry) => {
    if (entry.type === 'section') {
      groups.push({ section: entry, items: [] })
    } else if (groups.length === 0) {
      groups.push({ items: [entry] })
    } else {
      groups[groups.length - 1].items.push(entry)
    }

    return groups
  }, [])
}

export const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(
  (
    { logo, entries, bottomItems, className, collapsed = false, headerAction, tenantSwitcher },
    ref,
  ) => {
    const groups = React.useMemo(() => groupEntries(entries), [entries])

    return (
      <div
        ref={ref}
        className={cn(
          'flex h-full flex-col border-r border-main-border-default bg-main-background-card',
          collapsed ? 'w-16' : 'w-45',
          className,
        )}
        data-testid='sidebar'
      >
        <div
          className={cn(
            'flex h-13 shrink-0 items-center gap-2',
            'px-4',
            collapsed ? 'justify-center' : 'justify-between',
          )}
        >
          {logo}
          {!collapsed && headerAction != null ? headerAction : null}
        </div>

        {tenantSwitcher !== undefined && (
          <div className={cn('shrink-0 px-4 pb-2')}>{tenantSwitcher}</div>
        )}

        <nav
          id='sidebar-nav'
          className={cn('flex min-h-0 flex-1 flex-col overflow-y-auto pb-2 pt-9')}
          aria-label='Main navigation'
        >
          <div className={cn('flex flex-col gap-0.5 px-2')}>
            {groups.map((group, groupIndex) => {
              const renderItems = () =>
                group.items.map((item) => (
                  <SidebarNavItem
                    key={item.href}
                    icon={item.icon}
                    label={item.label}
                    href={item.href}
                    collapsed={collapsed}
                    disabled={item.disabled}
                    trackingEvent='sidebar_item_clicked'
                    extraTrackingProps={item.extraTrackingProps}
                  />
                ))

              return (
                <React.Fragment key={groupIndex}>
                  {group.section != null && (
                    <SidebarSection title={group.section.title} collapsed={collapsed} />
                  )}
                  {group.section?.title ? (
                    <div role='group' aria-label={group.section.title}>
                      {renderItems()}
                    </div>
                  ) : (
                    renderItems()
                  )}
                </React.Fragment>
              )
            })}
          </div>
        </nav>

        <div className={cn('flex shrink-0 flex-col gap-0.5 px-2 pb-4')}>
          {bottomItems.map((item) => (
            <SidebarNavItem
              key={item.href}
              icon={item.icon}
              label={item.label}
              href={item.href}
              collapsed={collapsed}
              trackingEvent='sidebar_item_clicked'
              extraTrackingProps={item.extraTrackingProps}
            />
          ))}
        </div>
      </div>
    )
  },
)
Sidebar.displayName = 'Sidebar'
